import { Chip, keyframes } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { RollChangeCountdownData } from "../models/MillOverview2";

type Props = {
	data: RollChangeCountdownData | null;
};

const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.5; }
`;

const glow = (color: string) => keyframes`
	0%, 100% { box-shadow: 0 0 0 0 ${color}; }
	50% { box-shadow: 0 0 8px 2px ${color}; }
`;

/**
 * Displays a countdown chip showing time remaining (or overdue) for an active roll change.
 * Visible only when StatusCode = 'I' AND FirstBilletTimestamp IS NULL.
 * RemainingMinutes is pre-calculated by the SP (excludes D/M/H overlap).
 * Displayed value is rounded DOWN to nearest 5 min.
 *
 * Animations by state:
 * - Success (green): static icon
 * - Warning (orange): border pulse
 * - Error (red): border pulse + glow
 */
export const RollChangeCountdown = ({ data }: Props) => {
	if (!data) return null;

	const {
		StatusCode,
		FirstBilletTimestamp,
		RemainingMinutes,
		IsPaused,
		WarningThresholdMinutes,
	} = data;

	// Only show when roll change is in progress
	if (StatusCode !== "I" || FirstBilletTimestamp !== null) return null;
	if (RemainingMinutes == null) return null;

	// Round down to nearest 5 minutes
	const absMinutes = Math.floor(Math.abs(RemainingMinutes) / 5) * 5;

	const days = Math.floor(absMinutes / (24 * 60));
	const hours = Math.floor((absMinutes % (24 * 60)) / 60);
	const mins = absMinutes % 60;
	const parts: string[] = [];
	if (days > 0) parts.push(`${days}d`);
	if (hours > 0 || days > 0) parts.push(`${hours}h`);
	parts.push(`${mins}m`);
	const timeStr = parts.join(" ");

	const isOverdue = RemainingMinutes < 0;

	// PAUSED state: RC is active but a D/M/H event is also active
	if (IsPaused) {
		const pausedSuffix = isOverdue ? "OVER" : "REMAINING";
		return (
			<Chip
				data-test-id="roll-change-countdown"
				icon={<SwapHorizIcon />}
				label={`ROLL CHANGE PAUSED \u2022 ${timeStr} ${pausedSuffix}`}
				size="small"
				variant="outlined"
				sx={{
					fontWeight: 700,
					fontSize: "0.7rem",
					borderRadius: 1,
					opacity: 0.7,
					borderColor: "#9E9E9E",
					backgroundColor: "#F5F5F5",
					color: "#757575",
					"& .MuiChip-label": { color: "#757575" },
					"& .MuiChip-icon": { color: "#9E9E9E" },
				}}
			/>
		);
	}

	const isWarning =
		!isOverdue && RemainingMinutes <= (WarningThresholdMinutes ?? 10);
	const label = isOverdue
		? `ROLL CHANGE ${timeStr} OVER`
		: `ROLL CHANGE ${timeStr} REMAINING`;

	const colorMap = {
		success: {
			border: "#4CAF50",
			bg: "#E8F5E9",
			text: "#1B5E20",
			icon: "#2E7D32",
		},
		warning: {
			border: "#FF9800",
			bg: "#FFF3E0",
			text: "#E65100",
			icon: "#EF6C00",
		},
		error: {
			border: "#F44336",
			bg: "#FFEBEE",
			text: "#B71C1C",
			icon: "#C62828",
		},
	};
	const colors = isOverdue
		? colorMap.error
		: isWarning
			? colorMap.warning
			: colorMap.success;

	return (
		<Chip
			data-test-id="roll-change-countdown"
			icon={<SwapHorizIcon />}
			label={label}
			size="small"
			variant="outlined"
			sx={{
				fontWeight: 700,
				fontSize: "0.7rem",
				borderRadius: 1,
				borderColor: colors.border,
				backgroundColor: colors.bg,
				color: colors.text,
				"& .MuiChip-label": { color: colors.text },
				"& .MuiChip-icon": { color: colors.icon },
				animation: isOverdue
					? `${pulse} 1s ease-in-out infinite`
					: isWarning
						? `${pulse} 2s ease-in-out infinite`
						: "none",
				...(isOverdue && {
					animationName: `${pulse}, ${glow("rgba(211, 47, 47, 0.4)")}`,
					animationDuration: "1s, 1.5s",
					animationIterationCount: "infinite, infinite",
					animationTimingFunction: "ease-in-out, ease-in-out",
				}),
			}}
		/>
	);
};
